import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Task } from '@shared/models/task.model';
import { ListTaskEventType, ListTaskService } from '@shared/modules/list/services/list-task.service';
import { TaskController } from '@shared/controllers/task.controller';
import { Employee } from '@shared/models/employee.model';
import { SnackBarService } from '@core/services/snackbar.service';
import { ProjectTaskAPIService } from '@modules/projects/shared/services/project-task-api.service';
import { AssignSearchDropdown } from '@modules/projects/shared/models/assign-search-dropdown.model';
import { publicFile } from '@shared/helpers/public-file.helper';
import { Contact } from '@modules/contacts/shared/models/contact.model';
import { EmployeeService } from '@shared/services/employee.service';
import { ContactService } from '@modules/contacts/shared/services/contact.service';
import { combineLatest } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ListResponse } from '@shared/modules/list/model/list-response.model';
import { EmployeeSearchItem } from '@shared/models/employee-search-item.model';

@Component({
  selector: 'assign-search-dropdown',
  templateUrl: './assign-search-dropdown.component.html',
  styleUrls: ['./assign-search-dropdown.component.scss']
})
export class AssignSearchDropdownComponent implements OnInit, AfterViewInit, OnDestroy {
  formGroup = new UntypedFormGroup({
    search: new UntypedFormControl('')
  });
  searchTimeout;
  assignSearchDropdown: AssignSearchDropdown;
  loading: boolean = false;

  @Input() task: Task;
  @Output('onTaskUpdate') onTaskUpdate: EventEmitter<Task> = new EventEmitter<Task>();
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  constructor(
    private listTaskService: ListTaskService,
    private s: SnackBarService,
    private projectTaskApiService: ProjectTaskAPIService,
    private changes: ChangeDetectorRef,
    private employeeService: EmployeeService,
    private contactService: ContactService
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit() {
    this.searchInput?.nativeElement?.focus();
  }

  toggleEmployeeAssignment(employee: Employee) {
    const ctrl = new TaskController(this.task);

    const assignedToIds = this.isEmployeeAssigned(employee)
      ? this.task.assignedTo.map((item) => item.id).filter((id) => id !== employee.id)
      : [employee.id, ...this.task.assignedTo.map((item) => item.id)];

    this.updateTaskAssignments(ctrl, assignedToIds, this.task.assignedEmails || []);
  }

  toggleContactAssignment(contact: Contact) {
    const ctrl = new TaskController(this.task);

    const assignedToIds = [...this.task.assignedTo.map((item) => item.id)];
    const assignedEmails = [...(this.task.assignedEmails || [])];

    if (contact.employee?.id) {
      if (this.isContactAssigned(contact)) {
        this.updateTaskAssignments(
          ctrl,
          assignedToIds.filter((id) => id !== contact.employee.id),
          assignedEmails
        );
      } else {
        this.updateTaskAssignments(ctrl, [contact.employee.id, ...assignedToIds], assignedEmails);
      }
      return;
    }

    if (!contact.email) return;

    if (this.isContactAssigned(contact)) {
      this.updateTaskAssignments(
        ctrl,
        assignedToIds,
        assignedEmails.filter((email) => email !== contact.email)
      );
    } else {
      this.updateTaskAssignments(ctrl, assignedToIds, [contact.email, ...assignedEmails]);
    }
  }

  imageUrl(url: string) {
    return publicFile(url, 100);
  }

  searchEmployee() {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.loading = true;
      this.changes.detectChanges();
      if (this.task.project) {
        this.searchInProject();
      } else {
        this.searchInCompany();
      }
    }, 200);
  }

  getQuery() {
    return this.formGroup.get('search').value.trim();
  }

  searchInProject() {
    this.projectTaskApiService
      .getEmployeeToAssigneeToTask(this.getQuery(), this.task, this.task.project)
      .subscribe({
        next: (res: AssignSearchDropdown) => {
          this.assignSearchDropdown = this.mergeSearchResults(res.employees, res.contacts);
          this.changes.detectChanges();
        },
        error: () => {
          this.s.defaultError();
        }
      })
      .add(() => {
        this.loading = false;
        this.changes.detectChanges();
      });
  }

  searchInCompany() {
    const employeeSubscribe = this.employeeService.getEmployees(this.getQuery());
    const contactsSubscribe = this.contactService.search(this.getQuery());
    combineLatest([employeeSubscribe, contactsSubscribe])
      .pipe(
        map((res: [Employee[], ListResponse<Contact>]) => {
          this.assignSearchDropdown = this.mergeSearchResults(res[0], res[1].records);
          this.changes.detectChanges();
        }),
        catchError((error) => {
          this.s.defaultError();
          throw error;
        })
      )
      .subscribe(() => {
        this.loading = false;
        this.changes.detectChanges();
      });
  }

  isEmployeeAssigned(employee: Employee): boolean {
    return this.task?.assignedTo?.some((item: EmployeeSearchItem) => item.id === employee.id);
  }

  isContactAssigned(contact: Contact): boolean {
    if (contact.employee?.id) {
      return this.task?.assignedTo?.some((item: EmployeeSearchItem) => item.id === contact.employee.id);
    }

    return !!contact.email && this.task?.assignedEmails?.includes(contact.email);
  }

  clearSearch(): void {
    this.formGroup.get('search').setValue('');
    this.searchEmployee();
  }

  private updateTaskAssignments(ctrl: TaskController, assignToIds: number[], assignedEmails: string[]) {
    this.changes.detectChanges();

    ctrl.assignToTask(this.task, assignToIds, assignedEmails).subscribe({
      next: (tasks: Task[]) => {
        this.task = new Task(tasks[0]);
        this.assignSearchDropdown = this.mergeSearchResults(
          this.assignSearchDropdown?.employees || [],
          this.assignSearchDropdown?.contacts || []
        );
        this.onTaskUpdate.emit(this.task);
        this.listTaskService.emitter.emit({ type: ListTaskEventType.ASSIGNEE_CHANGE });
        this.listTaskService.emitter.emit({
          type: ListTaskEventType.UPDATE_TASK,
          data: { task: this.task, ignoreTasksRefresh: true }
        });
      },
      error: () => {
        this.s.error('Projects.assignedError');
      }
    });
  }

  private mergeSearchResults(employees: Employee[], contacts: Contact[]) {
    const employeeMap = new Map<number, Employee>();
    const contactMap = new Map<string, Contact>();

    [...employees, ...this.getAssignedEmployees()].forEach((employee) => {
      if (!employee?.id) return;
      employeeMap.set(employee.id, new Employee(employee));
    });

    [...contacts, ...this.getAssignedContacts()].forEach((contact) => {
      const key = this.getContactKey(contact);
      if (!key) return;
      contactMap.set(key, new Contact(contact));
    });

    return new AssignSearchDropdown({
      employees: Array.from(employeeMap.values()).sort((first, second) =>
        this.compareByAssignedState(this.isEmployeeAssigned(first), this.isEmployeeAssigned(second))
      ),
      contacts: Array.from(contactMap.values()).sort((first, second) =>
        this.compareByAssignedState(this.isContactAssigned(first), this.isContactAssigned(second))
      )
    });
  }

  private compareByAssignedState(firstAssigned: boolean, secondAssigned: boolean): number {
    if (firstAssigned === secondAssigned) return 0;
    return firstAssigned ? -1 : 1;
  }

  private getAssignedEmployees(): Employee[] {
    return (this.task?.assignedTo || []).map((employee) => new Employee(employee));
  }

  private getAssignedContacts(): Contact[] {
    const employeeContacts = (this.task?.assignedTo || [])
      .filter((employee: EmployeeSearchItem) => !!employee?.userPerson?.email)
      .map(
        (employee: EmployeeSearchItem) =>
          new Contact({
            fullName: employee.userPerson.previewName,
            email: employee.userPerson.email,
            employee
          })
      );

    const emailContacts = (this.task?.assignedEmails || []).map(
      (email: string) =>
        new Contact({
          fullName: email,
          email
        })
    );

    return [...employeeContacts, ...emailContacts];
  }

  private getContactKey(contact: Contact): string {
    if (contact?.employee?.id) return `employee-${contact.employee.id}`;
    if (contact?.email) return `email-${contact.email}`;
    if (contact?.id) return `contact-${contact.id}`;
    return '';
  }

  ngOnDestroy() {
    this.assignSearchDropdown = null;
  }
}
